import React, { FC, FormEvent } from 'react';
import {
  AutoComplete,
  AutoCompleteProps,
  AutoCompleteChangeEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import classNames from 'classnames';
import styles from './AutoCompleteField.module.scss';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

interface SyntheticEventTarget {
  name: string;
  id: string;
  value: string;
}

interface SyntheticEvent extends FormEvent<Element> {
  target: EventTarget & SyntheticEventTarget;
  value: string;
}

type PickedProps =
  | 'value'
  | 'suggestions'
  | 'completeMethod'
  | 'field'
  | 'onChange'
  | 'dropdown'
  | 'placeholder'
  | 'id'
  | 'required'
  | 'className'
  | 'onFocus'
  | 'name'
  | 'onSelect';

type AutoCompleteFieldProps = Pick<AutoCompleteProps, PickedProps> & {
  label?: string;
  legend?: string;
  error?: string;
  isLoading?: boolean;
  disabled?: boolean;
  labelClassname?: string;
};

const AutoCompleteField: FC<AutoCompleteFieldProps> = ({
  value,
  suggestions,
  completeMethod,
  field,
  onChange,
  onSelect,
  dropdown,
  placeholder,
  id,
  label,
  legend,
  required,
  className,
  error,
  onFocus,
  isLoading,
  disabled,
  name,
  labelClassname,
}) => {
  const itemTemplate = (
    item: { label: string; name: string; legend: string } | string
  ) => {
    if (typeof item === 'string') {
      return <span>{item}</span>;
    }
    return <span>{item.label ?? item.name ?? ''}</span>;
  };
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    const syntheticEvent: SyntheticEvent = {
      ...e,
      target: {
        ...e.currentTarget,
        name: name || '',
        id: id || '',
        value: null as unknown as string,
      },
      value: null as unknown as string,
      preventDefault: () => {},
      stopPropagation: () => {},
    };
    if (onChange) {
      onChange(syntheticEvent as unknown as AutoCompleteChangeEvent);
    }
    if (onSelect) {
      onSelect(syntheticEvent as unknown as AutoCompleteSelectEvent);
    }
  };

  return (
    <div className={styles.autoCompleteField}>
      <label htmlFor={id} className={classNames(styles.label, labelClassname)}>
        {label} {required && '*'}
      </label>
      <span className={styles.legend}>{legend}</span>
      <div className={styles.inputWrapper}>
        <AutoComplete
          className={classNames(styles.input, className)}
          value={value}
          suggestions={suggestions}
          completeMethod={completeMethod}
          field={field}
          onChange={onChange}
          onSelect={onSelect}
          itemTemplate={itemTemplate}
          dropdown={dropdown}
          placeholder={placeholder}
          id={id}
          required={required}
          onFocus={onFocus}
          disabled={isLoading || disabled}
          name={name}
        />
        {value && (
          <button
            type="button"
            className={styles.clearButton}
            aria-label="Clear"
            onClick={handleClear}
          >
            &times;
          </button>
        )}
        {isLoading && <LoadingSpinner className={styles.loading} />}
      </div>
      {error ? <p className={styles.errorMessage}>{error}</p> : null}
    </div>
  );
};

export default AutoCompleteField;
